import { lazy, useMemo } from 'react';
import { useIntl } from 'react-intl';

const RescheduleMeeting = lazy(
  () => import('../views/scheduleMeeting/reschedule')
);
const SuccessReservation = lazy(() => import('../views/successReservation'));
const Home = lazy(() => import('./../views/home'));
const ScheduleMeetingForAll = lazy(() => import('../views/scheduleMeeting'));
const ServiceList = lazy(() => import('../views/serviceList'));
const PersonalData = lazy(() => import('../views/personalData'));
const AccountInformation = lazy(() => import('../views/accountInformation'));
const MyAppointment = lazy(() => import('../views/myAppointment'));
const LandingPage = lazy(() => import('../views/landingPage'));
const HomePage = lazy(() => import('../views/landingPage/homePage'));
const PricesPage = lazy(() => import('../views/landingPage/prices'));
const MessagePage = lazy(() => import('../views/messagePage'));
const FormPage = lazy(() => import('../views/formPage'));
const TermsAndConditions = lazy(
  () => import('../views/landingPage/termsAndConditions')
);
const ReservationsPolicy = lazy(
  () => import('../views/landingPage/reservationPolicy')
);
const LinkExpired = lazy(() => import('../views/linkExpired'));
const Voting = lazy(() => import('../views/scheduleMeeting/voting'));
const SuccessVoting = lazy(
  () => import('../views/scheduleMeeting/voting/votingSucceed')
);

export interface IRoute<T = any> {
  component: JSX.Element;
  path: string;
  icon?: JSX.Element;
  privileges: true;
  to?: (props?: T) => string;
  fullTitle?: (props?: T) => { name: string; link?: string }[];
  title?: () => string;
  sidebarTitle?: () => string;
}

export interface IRoutes {
  LandingPage: IRoute;
  HomePage: IRoute;
  PricesPage: IRoute;
  Home: IRoute;
  ScheduleMeeting: IRoute;
  Services: IRoute;
  personalData: IRoute;
  successReservation: IRoute;
  accountInformation: IRoute;
  myAppointment: IRoute;
  rescheduleMeeting: IRoute;
  successVoting: IRoute;
  MessagePage: IRoute;
  TermsAndConditions: IRoute;
  ReservationsPolicy: IRoute;
  FormPage: IRoute;
  LinkExpired: IRoute;
  Voting: IRoute;
}

export function useRoutesConst() {
  const { formatMessage } = useIntl();

  const ROUTES: IRoutes = useMemo(() => {
    const routes: IRoutes = {
      LandingPage: {
        component: <LandingPage />,
        path: '/',
        privileges: true,
        to: () => '/',
      },
      HomePage: {
        component: <HomePage />,
        path: '/',
        privileges: true,
        to: () => '/',
      },
      PricesPage: {
        component: <PricesPage />,
        path: '/pricing',
        privileges: true,
        to: () => '/pricing',
      },
      TermsAndConditions: {
        component: <TermsAndConditions />,
        path: '/terms-conditions',
        privileges: true,
        to: () => '/terms-conditions',
      },
      ReservationsPolicy: {
        component: <ReservationsPolicy />,
        path: '/privacy-policy',
        privileges: true,
        to: () => '/privacy-policy',
      },
      Home: {
        component: <Home />,
        path: '',
        privileges: true,
        to: () => '/:client',
        fullTitle: () => [{ name: formatMessage({ id: 'home' }) }],
        title: () => formatMessage({ id: 'home' }),
        sidebarTitle: () => formatMessage({ id: 'home' }),
      },
      Services: {
        component: <ServiceList />,
        path: '',
        privileges: true,
        to: () => `/:client`,
        fullTitle: () => [{ name: formatMessage({ id: 'Services' }) }],
        title: () => formatMessage({ id: 'Services' }),
        sidebarTitle: () => formatMessage({ id: 'Services' }),
      },
      ScheduleMeeting: {
        component: <ScheduleMeetingForAll />,
        path: ':event/:appointmentId?',
        privileges: true,
        to: (props) => `${props?.event}`,
        fullTitle: () => [{ name: formatMessage({ id: 'TimeAndDate' }) }],
        title: () => formatMessage({ id: 'TimeAndDate' }),
        sidebarTitle: () => formatMessage({ id: 'TimeAndDate' }),
      },
      rescheduleMeeting: {
        component: <RescheduleMeeting />,
        path: ':eventSlug/:appointmentId/cancel',
        privileges: true,
        to: (props) =>
          `${props?.client}/${props?.eventSlug}/${props.appointmentId}/cancel`,
      },
      personalData: {
        component: <PersonalData />,
        path: 'personal-data',
        privileges: true,
        to: () => `personal-data`,
      },
      successReservation: {
        component: <SuccessReservation />,
        path: 'success',
        privileges: true,
        to: () => `success`,
      },
      successVoting: {
        component: <SuccessVoting />,
        path: 'success-voting',
        privileges: true,
        to: () => `success-voting`,
      },
      accountInformation: {
        component: <AccountInformation />,
        path: 'account-information',
        privileges: true,
        to: () => `account-information`,
      },
      myAppointment: {
        component: <MyAppointment />,
        path: 'my-appointment',
        privileges: true,
        to: () => `my-appointment`,
      },
      MessagePage: {
        component: <MessagePage />,
        path: 'info/:id?',
        privileges: true,
        to: (props) => `info/${props.id}?`,
      },
      FormPage: {
        component: <FormPage />,
        path: 'form/:id?',
        privileges: true,
        to: (props) => `info/${props.id}?`,
      },
      Voting: {
        component: <Voting eventData={null} />,
        path: ':tenantSlug/tenant/:event',
        privileges: true,
        to: (props: any) => `voting/${props?.event}`,
      },
      LinkExpired: {
        component: <LinkExpired />,
        path: '/link-expired',
        privileges: true,
        to: () => '/link-expired',
      },
    };

    return routes;
  }, [formatMessage]);

  return { ROUTES };
}
